import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import pull from "lodash/pull";
import isEmpty from "lodash/isEmpty";
import find from "lodash/find";
import { useMutation, useQuery } from "react-query";
import { loadUsers } from "../../../queries/consumer/users";
import Apps from "../../../config/Apps";
import union from "lodash/union";
import { updateApplication } from "../../../queries/consumer/application";
import { createErrors } from "../../../queries/helpers";
import { useErrorHandler } from "react-error-boundary";
import { Link } from "react-router-dom";

const AppSettings = ({ queryClient, application, currentUser, toast }) => {
  const handleError = useErrorHandler();
  const [sandBoxChecked, setSandBoxChecked] = useState(true);
  const [availableApiProducts, setAvailableApiProducts] = useState([]);
  const [updatedApplication, setUpdatedApplication] = useState({
    name: application.name,
    sandbox: application.sandbox,
    userIds: application.users.map((user) => user.id),
    apiProducts: application.apiProducts,
  });

  const updateApp = useMutation(async (formData) => {
    await updateApplication(formData, application.id).then((data) => {
      queryClient.setQueryData(["application", application.id], data);
    });
  });

  const handleUpdateApp = async () => {
    await updateApp
      .mutateAsync(updatedApplication)
      .then(() => {
        toast.success([
          {
            title: "Application has been updated",
            body: updatedApplication.name,
          },
        ]);
      })
      .catch((error) => {
        toast.error([
          {
            title: "Failed to update application",
            body: `${error.response.data.errors
              .map((error) => error.detail)
              .join(", ")}`,
          },
        ]);
        return createErrors(error, "Failed to update application", handleError);
      });
  };

  const handleCheckboxChange = (event) => {
    const isChecked = event.target.checked;
    const item = event.target.value;
    if (isChecked) {
      setUpdatedApplication((updatedApplication) => {
        let newApiProducts = updatedApplication.apiProducts;
        return {
          ...updatedApplication,
          apiProducts: union([item], newApiProducts),
        };
      });
      setAvailableApiProducts((availableApiProducts) => {
        return availableApiProducts.map((el) =>
          el.name === item ? { ...el, checked: true } : el
        );
      });
    } else {
      setUpdatedApplication((updatedApplication) => {
        let newApiProducts = updatedApplication.apiProducts;
        return {
          ...updatedApplication,
          apiProducts: pull(newApiProducts, item),
        };
      });
      setAvailableApiProducts((availableApiProducts) => {
        return availableApiProducts.map((el) =>
          el.name === item ? { ...el, checked: false } : el
        );
      });
    }
  };

  useEffect(() => {
    const apiProducts = Apps.filter((app) => app.enabled === true);
    setAvailableApiProducts(
      apiProducts.map((ap) => {
        ap.checked = application.apiProducts.includes(ap.name);
        return ap;
      })
    );
    if (!application.sandbox) {
      setSandBoxChecked(false);
    }
  }, []);

  const {
    isLoading,
    isError,
    data: users,
  } = useQuery("users", () => loadUsers(), {
    keepPreviousData: true,
  });

  const handleAssignUser = (e, id) => {
    if (e.target.classList.value.includes("active")) {
      e.target.classList.remove("active");
      setUpdatedApplication((updatedApplication) => {
        let newUserIds = updatedApplication.userIds;
        return { ...updatedApplication, userIds: pull(newUserIds, id) };
      });
    } else {
      e.target.classList.add("active");
      setUpdatedApplication((updatedApplication) => {
        let newUserIds = updatedApplication.userIds;
        newUserIds.push(id);
        return { ...updatedApplication, userIds: newUserIds };
      });
    }
  };

  const handleAppNameChange = (e) => {
    const name = e.target.value;
    setUpdatedApplication((updatedApplication) => {
      return { ...updatedApplication, name: name };
    });
  };

  const isActiveUser = (userId) => {
    const user = find(application.users, { id: userId });
    if (user) {
      return "active";
    }
  };

  const handleEnvChange = (e) => {
    const sandbox = e.target.checked;
    setSandBoxChecked(sandbox);
    setUpdatedApplication((updatedApplication) => {
      return { ...updatedApplication, sandbox: sandbox };
    });
  };

  const disableEnvChange = () => {
    return !(
      currentUser.organization.verified && currentUser.type !== "Developer"
    );
  };

  return (
    <div id="settings-users" className="tab-pane w-100">
      <div className="row">
        <div className="col-12 col-lg-6 col-xl-5 pr-lg-5 border-lg-right mb-3">
          <h4 className="mb-4">App settings</h4>
          <div className="fin-col mw-400">
            <label>App name</label>
            <input
              className="fin-control"
              type="text"
              value={updatedApplication.name}
              onChange={(e) => handleAppNameChange(e)}
            />
          </div>
          <div className="fin-col">
            <label>Subscribed APIs</label>
            {availableApiProducts.map((app, index) => (
              <label key={index} className="custom-checkbox mb-2">
                <input
                  type="checkbox"
                  value={app.name}
                  checked={app.checked}
                  onChange={(e) => handleCheckboxChange(e)}
                />
                <span>{app.label}</span>
              </label>
            ))}
          </div>
          <div className="fin-col">
            <label>Environment</label>
            <label className="custom-checkbox mb-2">
              <input
                type="checkbox"
                disabled={disableEnvChange()}
                checked={sandBoxChecked}
                onChange={(e) => handleEnvChange(e)}
              />
              <span>SandBox</span>
            </label>
          </div>
        </div>

        <div className="col-12 col-lg-6 col-xl-7 pl-lg-5 mb-3">
          <h4 className="mb-4">App users</h4>

          <p className="text-small mb-4">
            Toggle the users you want assigned on this app
          </p>

          <div className="d-flex flex-wrap">
            {!isEmpty(users) && !isLoading && !isError && (
              <>
                {users.map((user, index) => (
                  <div key={index}>
                    {user.id === currentUser.id ? (
                      <a
                        key={user.id}
                        className="btn-tag btn-tag-sm active disabled"
                        onClick={(e) => handleAssignUser(e, user.id)}
                      >
                        {user.fullName}
                      </a>
                    ) : (
                      <a
                        key={user.id}
                        className={`btn-tag btn-tag-sm ${isActiveUser(
                          user.id
                        )}`}
                        onClick={(e) => handleAssignUser(e, user.id)}
                      >
                        {user.fullName}
                      </a>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>

          <p className="mt-4 text-small text-muted">
            You can invite more users from the{" "}
            <Link className="link-color" to="/organization">
              organization
            </Link>{" "}
            page.
          </p>
        </div>
      </div>

      <div className="mt-3">
        <button
          id="submit"
          className="btn btn-sm btn-main"
          disabled={isEmpty(updatedApplication.name)}
          onClick={() => handleUpdateApp()}
        >
          Update settings & Users
        </button>
      </div>
    </div>
  );
};

AppSettings.propTypes = {
  application: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
};

export default AppSettings;
